import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "./Field";

/**
 * Searchable combobox (§2.7): the replacement for free-text entity fields —
 * client/agent pickers must be bound to real records, never typed names.
 * options: [{ value, label, description? }]. Controlled by `value`/`onChange`.
 */
function Combobox({
  options,
  value,
  onChange,
  placeholder = "Search…",
  emptyMessage = "No matches",
  inputProps = {}
}) {
  const selected = useMemo(() => options.find((o) => o.value === value) || null, [options, value]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return options;
    }
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.description && o.description.toLowerCase().includes(q))
    );
  }, [options, query]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const select = (option) => {
    onChange(option.value, option);
    setOpen(false);
    setQuery("");
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[activeIndex]) {
        select(filtered[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
  };

  return (
    <div className="fk-combobox" ref={rootRef}>
      <Input
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        placeholder={placeholder}
        value={open ? query : selected ? selected.label : ""}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) {
            setOpen(true);
          }
        }}
        onKeyDown={onKeyDown}
        {...inputProps}
      />
      {open ? (
        <ul className="fk-combobox__list" role="listbox">
          {filtered.length === 0 ? (
            <li className="fk-combobox__empty">{emptyMessage}</li>
          ) : (
            filtered.map((option, i) => (
              <li
                key={option.value}
                role="option"
                aria-selected={option.value === value}
                className={`fk-combobox__option${i === activeIndex ? " fk-combobox__option--active" : ""}`}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  select(option);
                }}
              >
                {option.label}
                {option.description ? (
                  <span className="fk-combobox__option-desc">{option.description}</span>
                ) : null}
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

export default Combobox;
